import { useState, useRef, useEffect } from "react";
import confetti from "canvas-confetti";
import { initializeApp } from "firebase/app";
import { getDatabase, ref, onValue, push,set } from "firebase/database";

// 🔹 Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyA8yWYolK-WAb2B42AuIoeY59YyzSHqOVs",
  authDomain: "dailydash01-90bef.firebaseapp.com",
  databaseURL: "https://dailydash01-90bef-default-rtdb.firebaseio.com",
  projectId: "dailydash01-90bef",
  storageBucket: "dailydash01-90bef.appspot.com",
  messagingSenderId: "27747474270",
  appId: "1:27747474270:web:37b7d85e0cd2877cbccdcf",
};

// 🔹 Init Firebase
const app = initializeApp(firebaseConfig);
const db = getDatabase(app);

// 🔹 Built-in questions
const dailyQuestions = [
  { q: "What has to be broken before you can use it?", a: "egg" },
  { q: "I’m tall when I’m young, and short when I’m old. What am I?", a: "candle" },
  { q: "What month of the year has 28 days?", a: "all" },
  { q: "What is full of holes but still holds water?", a: "sponge" },
  { q: "What question can you never answer yes to?", a: "are you asleep" },
  { q: "What is always in front of you but can’t be seen?", a: "future" },
  { q: "What goes up but never comes down?", a: "age" },
];

type Question = { q: string; a: string };
type LeaderboardEntry = { name: string; timestamp: number; streak: number;};

export default function App() {
  const correctAudioRef = useRef<HTMLAudioElement | null>(null);
  const wrongAudioRef = useRef<HTMLAudioElement | null>(null);
  const newQuestionAudioRef = useRef<HTMLAudioElement | null>(null);

  const [name, setName] = useState("");
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState("");
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [streak, setStreak] = useState(0);
  const [customQuestions, setCustomQuestions] = useState<Question[]>([]);
  const [newQ, setNewQ] = useState("");
  const [newA, setNewA] = useState("");
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [volume, setVolume] = useState(0.8);
  
  useEffect(() => {
  const todayKey = `day-${new Date().toISOString().split("T")[0]}`; // e.g. "2025-09-18"
  const leaderboardRef = ref(db, todayKey);

  return onValue(leaderboardRef, (snapshot) => {
    setLoading(false);

    if (snapshot.exists()) {
      const raw = Object.values(snapshot.val()) as LeaderboardEntry[];
      const sorted = raw.sort((a, b) => a.timestamp - b.timestamp);

      setLeaderboard(sorted);
    } else {
      setLeaderboard([]);
    }
  });
}, []);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const qRef = ref(db, "questions");
    return onValue(qRef, (snapshot) => {
      if (snapshot.exists()) {
        const extraQs = Object.values(snapshot.val()) as Question[];
        setCustomQuestions(extraQs);
      }
    });
  }, []);

  const allQuestions = [...dailyQuestions, ...customQuestions];
  const todayIndex = new Date().getDate() % allQuestions.length;
  const todayQuestion: Question = allQuestions[todayIndex] ?? { q: "No question today.", a: "" };
  const todayKey = `day-${new Date().toISOString().split("T")[0]}`;

  useEffect(() => {
    const savedStreak = localStorage.getItem("dailyDashStreak");
    if (savedStreak) setStreak(parseInt(savedStreak));

    const savedTheme = localStorage.getItem("dailyDashTheme");
    if (savedTheme === "dark") setDarkMode(true);
  }, []);

  useEffect(() => {
    localStorage.setItem("dailyDashTheme", darkMode ? "dark" : "light");
  }, [darkMode]);

  useEffect(() => {
  const savedStreak = parseInt(localStorage.getItem("dailyDashStreak") || "0", 10);
  const lastPlayed = localStorage.getItem("dailyDashLastPlayed");
  const today = new Date().toDateString();

  if (!lastPlayed) {
    setStreak(0);
    return;
  }

  if (lastPlayed === today) {
    setStreak(savedStreak);
  } else {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (lastPlayed === yesterday.toDateString()) {
      setStreak(savedStreak);
    } else {
      setStreak(0);
      localStorage.setItem("dailyDashStreak", "0");
    }
  }
}, [setStreak]); // ✅ no more yellow line

  const normalize = (str: string) =>
    str.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

  const playCorrectSound = () => {
    if (correctAudioRef.current) {
      correctAudioRef.current.volume = volume;
      correctAudioRef.current.play().catch(console.error);
    }
  };

  const playWrongSound = () => {
    if (wrongAudioRef.current) {
      wrongAudioRef.current.volume = volume;
      wrongAudioRef.current.play().catch(console.error);
    }
  };

  const playNewQuestionSound = () => {
    if (newQuestionAudioRef.current) {
      newQuestionAudioRef.current.volume = volume;
      newQuestionAudioRef.current.play().catch(console.error);
    }
  };

  const checkAnswer = () => {
  if (!name.trim()) {
    setResult("⚠ Please enter your name!");
    return;
  }

  const isCorrect = normalize(answer) === normalize(todayQuestion.a);
  const today = new Date().toDateString();
  const lastPlayed = localStorage.getItem("dailyDashLastPlayed");

  if (isCorrect) {
    setResult("✅ Correct! You’re on today’s leaderboard!");
    playCorrectSound();
    confetti({ particleCount: 100, spread: 60, origin: { y: 0.6 } });
    setAnswer("");

    let newStreak = streak;

    if (lastPlayed !== today) {
      // only increment if they haven’t already answered today
      newStreak = streak + 1;
      setStreak(newStreak);
      localStorage.setItem("dailyDashStreak", newStreak.toString());
      localStorage.setItem("dailyDashLastPlayed", today);
    }

    // Add to leaderboard if not already there
    if (!leaderboard.some((e) => normalize(e.name) === normalize(name))) {
  // Save the user using their name as the key (prevents duplicates)
const userRef = ref(db, `${todayKey}/${name.trim()}`);
set(userRef, {
  name: name.trim(),
  timestamp: Date.now(),
  streak: newStreak
});
}
  } else {
    setResult("❌ Oops! Try again tomorrow.");
    playWrongSound();
    if (inputRef.current) {
      inputRef.current.classList.add("shake");
      setTimeout(() => inputRef.current?.classList.remove("shake"), 500);
    }
    setStreak(0);
    localStorage.setItem("dailyDashStreak", "0");
    localStorage.setItem("dailyDashLastPlayed", today);
    setAnswer("");
  }
};
  const addQuestion = () => {
    const q = newQ.trim();
    const a = newA.trim();
    if (!q || !a) {
      alert("⚠ Please fill in both fields.");
      return;
    }
    if (allQuestions.some((item) => normalize(item.q) === normalize(q))) {
      alert("⚠ This question already exists.");
      return;
    }
    const questionsRef = ref(db, "questions");
    push(questionsRef, { q, a });
    setNewQ("");
    setNewA("");
    playNewQuestionSound();
    alert("✅ Question added!");
  };

  const themeStyles = {
    backgroundColor: darkMode ? "#121212" : "#f9f9f9",
    color: darkMode ? "#f1f1f1" : "#222",
    transition: "all 0.3s ease",
    minHeight: "100vh",
    padding: 16,
    fontFamily: "Arial, sans-serif",
  };

  const inputStyle = {
    padding: 10,
    width: "100%",
    marginBottom: 8,
    borderRadius: 6,
    border: "1px solid #ccc",
    backgroundColor: darkMode ? "#1e1e1e" : "#fff",
    color: darkMode ? "#f1f1f1" : "#000",
    transition: "all 0.3s ease",
  };

  const buttonStyle = {
    padding: 10,
    width: "100%",
    borderRadius: 6,
    backgroundColor: darkMode ? "#4CAF50" : "#2196F3",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    transition: "transform 0.2s ease",
  };
const LightModeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="5" fill="#FFD700" />
    <g stroke="#FFD700" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="23" />
      <line x1="1" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="23" y2="12" />
      <line x1="4.2" y1="4.2" x2="6.3" y2="6.3" />
      <line x1="17.7" y1="17.7" x2="19.8" y2="19.8" />
      <line x1="4.2" y1="19.8" x2="6.3" y2="17.7" />
      <line x1="17.7" y1="6.3" x2="19.8" y2="4.2" />
    </g>
  </svg>
);

const DarkModeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M21 12.79A9 9 0 0111.21 3 7 7 0 0012 21a9 9 0 009-8.21z" fill="#f1f1f1" />
  </svg>
);

const VolumeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M3 9v6h4l5 5V4L7 9H3z" fill="#4CAF50" />
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.06c1.48-.74 2.5-2.26 2.5-4.03z" fill="#4CAF50" />
  </svg>
);
  return (
    <div
    style={{
      ...themeStyles,
      maxWidth: 600,
      margin: "auto",
      borderRadius: 16,
      boxShadow: darkMode
        ? "0 0 20px rgba(0,0,0,0.6)"
        : "0 0 20px rgba(0,0,0,0.1)",
      padding: 24,
      background: darkMode
        ? "linear-gradient(to bottom, #121212, #1e1e1e)"
        : "linear-gradient(to bottom, #f9f9f9, #eaeaea)",
    }}
  >

      <audio ref={correctAudioRef} src="https://www.dropbox.com/scl/fi/rmc6lmtcqye4fx3sjai3c/pop-win-casino-winning-398059.mp3?rlkey=67porrdwfbxwzkm28uudxs9a1&st=36b6cqbx&raw=1" preload="auto" />
      <audio ref={wrongAudioRef} src="https://www.dropbox.com/scl/fi/zl1pjtx08khdl16jxq9hg/fail-234710.mp3?rlkey=bf5jrzzyih92urnqwytoervg2&st=0l6aemd0&raw=1" preload="auto" />
      <audio ref={newQuestionAudioRef} src="https://www.dropbox.com/scl/fi/wnfz4zjlavq4ilaxttth9/ding-402325.mp3?rlkey=hx9fjqqiigd3zpgk7udcofhor&st=jz6q9t0f&raw=1" preload="auto" />

      {/* Dark Mode Toggle */}
    <button
      onClick={() => setDarkMode(!darkMode)}
      style={{
        ...buttonStyle,
        marginBottom: 16,
        backgroundColor: darkMode ? "#444" : "#555",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
      }}
    >
      {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
      {darkMode ? "Light Mode" : "Dark Mode"}
    </button>

      {/* Volume Control */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 16,
      }}
    >
      <VolumeIcon />
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={volume}
        onChange={(e) => setVolume(parseFloat(e.target.value))}
        style={{ flex: 1 }}
      />
    </div>


      {/* Title */}
    <h1
      style={{
        fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
        fontWeight: "bold",
        marginBottom: 16,
        textAlign: "center",
        lineHeight: 1.2,
      }}
    >
        🎯 Reddit Daily Dash — Test Your Brain With Daily Riddles & Puzzles!
      </h1>

      <p><strong>Question:</strong> {todayQuestion.q}</p>

      <input
        type="text"
        placeholder="Your name..."
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={inputStyle}
      />
      <input
        ref={inputRef}
        type="text"
        placeholder="Your answer..."
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") checkAnswer(); }}
        style={inputStyle}
      />

      <button
        onClick={checkAnswer}
        style={{
          ...buttonStyle,
          backgroundColor: "#4CAF50",
          marginBottom: 12,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        Submit Answer
      </button>

      <p style={{ marginTop: 12 }}>{result}</p>
      <p style={{ marginTop: 4, fontWeight: "bold" }}>🔥 Streak: {streak}</p>

      <div style={{ marginTop: 24, padding: 12, border: "1px solid #ccc", borderRadius: 8 }}>
        <h4>➕ Add a new question</h4>
        <input
          type="text"
          placeholder="Question..."
          value={newQ}
          onChange={(e) => setNewQ(e.target.value)}
          style={inputStyle}
        />
        <input
          type="text"
          placeholder="Answer..."
          value={newA}
          onChange={(e) => setNewA(e.target.value)}
          style={inputStyle}
        />
        <button
          onClick={addQuestion}
          style={{
            ...buttonStyle,
            backgroundColor: "#2196F3",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          Add Question
        </button>
      </div>

      <div style={{ marginTop: 20 }}>
  <h3>🏆 Today's Leaderboard</h3>
  {loading ? (
    <p>Loading leaderboard...</p>
  ) : (
    <div
      style={{
        marginTop: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {leaderboard.slice(0, 10).map((entry, idx) => {
        const medal =
          idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : "✨";

        return (
          <div
            key={idx}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 12px",
              borderRadius: 8,
              backgroundColor: darkMode ? "#2b2b2b" : "#f5f5f5",
              color: darkMode ? "#fff" : "#000",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              fontSize: "0.95rem",
              transition: "transform 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "scale(1.02)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = "scale(1)")
            }
          >
            {/* Medal */}
            <span style={{ fontSize: "1.2rem" }}>{medal}</span>

            {/* Name */}
            <span style={{ fontWeight: "bold", flex: 1, marginLeft: 8 }}>
              {entry.name}
            </span>

            {/* Streak */}
            <span
              style={{
                fontSize: "0.85rem",
                marginRight: 12,
                color: "#ff5722",
                fontWeight: "bold",
              }}
            >
              🔥 {entry.streak || 0}d
            </span>

            {/* Last Answer Time */}
            <span style={{ fontSize: "0.8rem", opacity: 0.7 }}>
              {new Date(entry.timestamp).toLocaleTimeString()}
            </span>
          </div>
        );
      })}
    </div>
  )}
</div>
    </div>
  );
}